/**
 * Block: Page top with product
 *
 * Two "Seisnode" node clips drift behind the centred heading. Each clip is shot
 * on black and composited with mix-blend-mode: screen (see the SCSS), so no
 * alpha channel is needed. We deliberately DON'T scrub frames — the videos just
 * loop; a GSAP ScrollTrigger drifts them up-and-out (with a fade) as the section
 * scrolls away, so the reader reaches the content below sooner.
 *
 * Playback is gated on viewport visibility (IntersectionObserver) and tab
 * visibility to save battery. Under prefers-reduced-motion nothing plays or
 * moves — the poster frame stands in. The block only runs on the front end; the
 * .twig omits data-ptwm in the editor so this is a no-op there.
 *
 * Assets are static theme files under /static/seisnode/, rebuilt with
 * bin/build-seisnode-video.sh.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { intersectionObserve } from './_intersection-observer.js';

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

class PageTopModel {
  constructor(section) {
    this.section = section;
    // Each node IS a <video> element (the block markup has no wrapper — a wrapper
    // would create a stacking context and break the screen blend; see the SCSS).
    this.videos = Array.from(section.querySelectorAll('[data-ptwm-node]'));

    this.inView = false;
    this.pageVisible = !document.hidden;
    this.st = null;

    this._onVisibility = this._onVisibility.bind(this);
    this._onMotionChange = this._onMotionChange.bind(this);
  }

  init() {
    if (!this.videos.length) return;

    intersectionObserve(this.section, 0, 0, (el, ratio) => {
      this.inView = ratio > 0;
      this._updatePlayback();
    });

    document.addEventListener('visibilitychange', this._onVisibility);

    this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (this.motionQuery && this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this._onMotionChange);
    }

    if (!reducedMotion()) this._setupScroll();
  }

  _updatePlayback() {
    if (reducedMotion() || !this.pageVisible || !this.inView) {
      this.videos.forEach((v) => v.pause());
    } else {
      this.videos.forEach((v) => {
        const p = v.play();
        if (p && p.catch) p.catch(() => {}); // ignore autoplay-policy rejections
      });
    }
  }

  // Drift the nodes up and outward (with a fade) across one viewport of scroll.
  // The base lean/scale lives on the inner <video>, so translating the outer
  // node here never fights the CSS rotation. The larger right node travels a
  // little further so both clear the frame at roughly the same time.
  _setupScroll() {
    const [left, right] = this.videos;
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.section,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.5,
      },
    });
    if (left)  tl.to(left,  { yPercent: -55, xPercent: -18, autoAlpha: 0.15, ease: 'none' }, 0);
    if (right) tl.to(right, { yPercent: -75, xPercent: 22,  autoAlpha: 0.15, ease: 'none' }, 0);
    this.st = tl.scrollTrigger;
  }

  _onVisibility() {
    this.pageVisible = !document.hidden;
    this._updatePlayback();
  }

  _onMotionChange() {
    if (reducedMotion()) {
      if (this.st) { this.st.kill(); this.st = null; }
      gsap.set(this.videos, { clearProps: 'transform,opacity,visibility' });
    } else if (!this.st) {
      this._setupScroll();
    }
    this._updatePlayback();
  }
}

const sections = document.querySelectorAll('section.page-top-with-model[data-ptwm]');
sections.forEach((s) => new PageTopModel(s).init());
